/**
 * Scribe  Client
 *
 * This module is responsible for transmitting data to Scribe.
 *
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 *
 * TODO: PT:29425907 Enforce correct modules are used
 */
/* eslint-env browser */

import ScribeEvent from './scribe_event';
import SCRIBE_EVENTS from './scribe_event_constants';
import ScribePixel from './scribe_pixel';
import ScribeStatsTracker from './scribe_stats_tracker';

/**
 * Gets link element from URL string
 *
 * TODO: Use URL class when we no longer support IE
 *
 * @param url String of url
 *
 * @return {element} <a> tag
 */
const getLinkElement = url => {
  // do not create element on SSR
  if (typeof document === 'undefined') {
    return null;
  }

  if (!url) {
    return url;
  }

  const link = document.createElement('a');
  link.href = url;
  return link;
};

/**
 * Determine if the given domain is a sub-domain of the given root domain.
 *
 * @param domain the potential sub-domain we want to test
 * @param rootDomain rootDomain we are comparing domain against
 *
 * @returns {boolean} if domain is equal to or a sub-domain of domain
 */
const isSubDomain = (domain, rootDomain) => {
  const subDomainParts = domain.split('.');
  const rootDomainParts = rootDomain.split('.');

  if (rootDomainParts.length > subDomainParts.length) {
    return false;
  }

  return subDomainParts.slice(-rootDomainParts.length).join('.') === rootDomain;
};

const isAdmin = (adminUrl, hostname) =>
  adminUrl && isSubDomain(hostname, adminUrl.hostname);

const isExtranet = (extranetUrl, hostname) =>
  extranetUrl && isSubDomain(hostname, extranetUrl.hostname);

/**
 * Computes beacon url, will be null if the current hostname is not valid
 *
 * @param isImpression whether we want the impression or default endpoint
 *
 * @returns {String|null}
 *
 */
const getBeaconUrl = (config, isImpression) => {
  if (typeof window === 'undefined') {
    return null;
  }

  let hostname = null;
  let endpoint = null;

  const ADMIN_URL = getLinkElement(config.adminDomain);

  const EXTRANET_URL = getLinkElement(config.extranetDomain);

  let scheme = 'https';
  if (config.localHost) {
    // For local testing only
    const {localHost} = config;
    hostname = `${localHost.hostname}:${localHost.port}`;
    endpoint = localHost.endpoint;
    scheme = 'http';
  } else if (config.storeDomain) {
    hostname = `t.${config.storeDomain}`;
    endpoint = isImpression ? 'imp.php' : 'b.php';
  } else if (isAdmin(ADMIN_URL, window.location.hostname)) {
    hostname = ADMIN_URL.hostname;
    endpoint = 'beacon.php';
  } else if (isExtranet(EXTRANET_URL, window.location.hostname)) {
    hostname = `t.${EXTRANET_URL.hostname}`;
    endpoint = 'beacon.php';
  } else {
    return null;
  }
  return `${scheme}://${hostname}/${endpoint}`;
};

/**
 * Construct a ScribeClient.  This module is reponsible for sending raw
 * data to scribe.
 *
 * @param {ScribeStatsTracker} statsTracker object used to collect stats about
 * requests made to scribe; can be any object that responds to 'trackRequest'
 * @return {ScribeClient}
 */
class ScribeClient {
  /* eslint-disable-next-line max-params */
  constructor(config, statsTracker, PixelLoader, Event) {
    this.config = config;
    this.statsTracker = statsTracker || new ScribeStatsTracker();
    this.PixelLoader = PixelLoader || ScribePixel;
    this.Event = Event || ScribeEvent;

    this.beaconUrl = getBeaconUrl(this.config, false);
    this.impressionUrl = getBeaconUrl(this.config, true);
  }

  sendBatchedEvents(eventData) {
    const pixel = new this.PixelLoader(this.config, this.beaconUrl, eventData);
    return pixel.post();
  }

  /**
   * Send an event to Scribe
   *
   * @param {Object} eventData Event data to send
   * @param {Boolean} isImpression if the Event is an ImpressionEvent
   * @return {Promise}
   */
  sendEvent(eventData, isImpression) {
    const event = new this.Event(
      this.encodeEventData(eventData),
      this.config.transactionID,
      this.config.storeID
    );
    const pixel = new this.PixelLoader(
      this.config,
      isImpression ? this.impressionUrl : this.beaconUrl,
      event
    );
    const promise = pixel.load();
    this.statsTracker.trackRequest(promise);
    return promise;
  }

  encodeEventData(eventData) {
    let formatEventData = eventData;
    switch (formatEventData.eventType) {
      case SCRIBE_EVENTS.MALWARE_DETECTED:
      case SCRIBE_EVENTS.WEB_ACTION:
        if (this.checkForObjectOrArray(formatEventData.actionData)) {
          const {actionData, ...rest} = formatEventData;
          formatEventData = {
            actionData: JSON.stringify(actionData),
            ...rest,
          };
        }
        break;
      case SCRIBE_EVENTS.WEB_CLICK: {
        if (this.checkForObjectOrArray(formatEventData.clickLocMeta)) {
          const {clickLocMeta, ...rest} = formatEventData;
          formatEventData = {
            clickLocMeta: JSON.stringify(clickLocMeta),
            ...rest,
          };
        }
        // add defaults for missing required fields
        const {
          clickLoc = 'default',
          clickType = 'js-track-event',
          ...payload
        } = formatEventData;
        formatEventData = {
          clickLoc,
          clickType,
          ...payload,
        };
        break;
      }

      default:
        break;
    }
    return formatEventData;
  }

  checkForObjectOrArray(data) {
    return (data !== null && typeof data === 'object') || Array.isArray(data);
  }

  /**
   * Use sendBeacon API to send pixel fire event to Scribe
   *
   * @param {Object}  eventData    Event data with all required information for a pixel fire
   * @param {Boolean} isImpression Whether it is an impression
   */
  sendBatchedBeaconPost(eventData, isImpression = false) {
    const scribeEvent = new this.Event(eventData);
    const pixel = new this.PixelLoader(
      this.config,
      isImpression ? this.impressionUrl : this.beaconUrl,
      scribeEvent
    );
    return pixel.sendBatchedBeaconPost();
  }
}

export default ScribeClient;
